import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Space, Spin, message } from 'antd';
import { CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import { HOME_BANNERS_ROUTES } from '../home-banners/constants';
import {
    createHomeBanner,
    deleteHomeBanner,
    getHomeBanner,
    getHomeBanners,
    updateHomeBanner,
} from './actions';
import {
    getNextDisplayOrder,
    getOrderOptions,
    toFormValues,
    toPayload,
} from './helpers';
import HomeBannerForm from './home-banner-form';
import { HomeBannerFormValues } from './types';

type Mode = 'create' | 'edit';

type Props = {
    mode: Mode;
    bannerId?: string;
};

type EditorCopy = {
    title: string;
    submit: string;
    success: string;
    error: string;
};

const copy: Record<Mode, EditorCopy> = {
    create: {
        title: 'Создать баннер главной страницы',
        submit: 'Создать',
        success: 'Баннер создан',
        error: 'Не удалось создать баннер',
    },
    edit: {
        title: 'Редактировать баннер главной страницы',
        submit: 'Сохранить',
        success: 'Баннер обновлён',
        error: 'Не удалось обновить баннер',
    },
};

const HomeBannerEditor = ({ mode, bannerId }: Props) => {
    const [form] = Form.useForm<HomeBannerFormValues>();
    const [loading, setLoading] = useState(false);
    const [recordLoading, setRecordLoading] = useState(mode === 'edit');
    const [orderOptions, setOrderOptions] = useState<number[]>([1]);

    const isEdit = mode === 'edit';

    useEffect(() => {
        if (isEdit) {
            if (!bannerId) {
                message.error('Не удалось определить ID баннера');
                setRecordLoading(false);
                return;
            }

            setRecordLoading(true);
            Promise.all([getHomeBanner(bannerId), getHomeBanners()])
                .then(([{ data: banner }, { data: list }]) => {
                    const items = list.content || [];
                    setOrderOptions(getOrderOptions(items.length));
                    form.setFieldsValue(toFormValues(banner));
                })
                .catch((error) => {
                    console.error(error);
                    message.error('Не удалось загрузить баннер');
                })
                .finally(() => setRecordLoading(false));

            return;
        }

        getHomeBanners()
            .then(({ data }) => {
                const items = data.content || [];
                const nextOrder = getNextDisplayOrder(items);

                setOrderOptions(getOrderOptions(items.length + 1));
                form.setFieldsValue({ displayOrder: nextOrder });
            })
            .catch(() => {
                setOrderOptions([1]);
                form.setFieldsValue({ displayOrder: 1 });
            });
    }, [bannerId, form, isEdit]);

    const handleSubmit = async (values: HomeBannerFormValues) => {
        setLoading(true);

        try {
            if (isEdit) {
                if (!bannerId) {
                    message.error('Не удалось определить ID баннера');
                    return;
                }

                await updateHomeBanner(bannerId, toPayload(values));
            } else {
                await createHomeBanner(toPayload(values));
            }

            message.success(copy[mode].success);
            window.location.href = HOME_BANNERS_ROUTES.list;
        } catch (error) {
            console.error(error);
            message.error(copy[mode].error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        if (!bannerId) {
            message.error('Не удалось определить ID баннера');
            return;
        }

        Modal.confirm({
            title: 'Удалить баннер?',
            okText: 'Удалить',
            okButtonProps: { danger: true },
            cancelText: 'Отмена',
            onOk: async () => {
                try {
                    setLoading(true);
                    await deleteHomeBanner(bannerId);
                    message.success('Баннер удалён');
                    window.location.href = HOME_BANNERS_ROUTES.list;
                } catch (error) {
                    console.error(error);
                    message.error('Не удалось удалить баннер');
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    return (
        <Spin spinning={recordLoading}>
            <h2>{copy[mode].title}</h2>
            <HomeBannerForm
                form={form}
                orderOptions={orderOptions}
                onFinish={handleSubmit}
            />
            <Space
                wrap
                className="home-banners-admin__form-actions"
            >
                <Button
                    icon={<CheckOutlined />}
                    className="home-banners-admin__button--save"
                    onClick={() => form.submit()}
                    loading={loading}
                >
                    {copy[mode].submit}
                </Button>
                {isEdit && (
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        className="home-banners-admin__button--delete"
                        onClick={handleDelete}
                        loading={loading}
                    >
                        Удалить
                    </Button>
                )}
            </Space>
        </Spin>
    );
};

export default HomeBannerEditor;
